import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { jobAPI } from '../../../services';
import { 
  Briefcase, Users, Clock, AlertTriangle, TrendingUp, 
  CheckCircle, XCircle, UserPlus, Calendar, Plus
} from 'lucide-react';
import JobFormModal from './JobFormModal';
import JobDetailModal from './JobDetailModal';
import { useNavigate } from 'react-router-dom';

const StatCard = ({ title, value, icon: Icon, color = 'blue', subtext }) => (
  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
        {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
      </div>
      <div className={`p-3 rounded-full bg-${color}-100`}>
        <Icon className={`w-6 h-6 text-${color}-600`} />
      </div>
    </div>
  </div>
);

const RecruitmentDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [tatBreachedJobs, setTatBreachedJobs] = useState([]);
  const [recentJobs, setRecentJobs] = useState([]);
  const [error, setError] = useState('');
  const [showJobModal, setShowJobModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await jobAPI.getDashboard();
      if (response.status === 'success') {
        setStats(response.data.stats);
        setTatBreachedJobs(response.data.tatBreachedJobs || []);
        setRecentJobs(response.data.recentJobs || []);
      }
    } catch (err) {
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  const roleLabel = {
    hod: 'Head of Department',
    hr_head: 'HR Head',
    ta_executive: 'TA Executive',
    admin: 'Administrator',
    employee: 'HOD'
  };

  const handleJobCreated = () => {
    setShowJobModal(false);
    fetchDashboard();
  };

  const handleViewJob = (job) => {
    setSelectedJob(job);
    setShowDetailModal(true);
  };

  // Check if user can create job requests (HOD of dept or admin/hr_head)
  const canCreateJob = user?.isHOD || ['admin', 'hr_head', 'hod'].includes(user?.role);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Recruitment Dashboard</h1>
          <p className="text-gray-500 mt-1">
            Welcome back, {user?.name} ({roleLabel[user?.role] || user?.role})
          </p>
        </div>
        {canCreateJob && (
          <button
            onClick={() => setShowJobModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Create Job Request
          </button>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          title="Open Positions" 
          value={stats?.jobs?.approved || 0} 
          icon={Briefcase}
          color="blue"
          subtext={`${stats?.jobs?.pending || 0} pending approval`}
        />
        <StatCard 
          title="Total Candidates" 
          value={stats?.candidates?.total || 0} 
          icon={Users}
          color="purple"
          subtext={`${stats?.candidates?.sourced || 0} sourced`}
        />
        <StatCard 
          title="Offers Released" 
          value={stats?.candidates?.offered || 0} 
          icon={CheckCircle}
          color="green"
        />
        <StatCard 
          title="Joined" 
          value={stats?.candidates?.joined || 0} 
          icon={UserPlus}
          color="emerald"
        />
      </div>

      {/* TAT Breached Warning */}
      {tatBreachedJobs.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <h3 className="font-semibold text-red-800">TAT Breached Jobs ({tatBreachedJobs.length})</h3>
          </div>
          <div className="space-y-2">
            {tatBreachedJobs.slice(0, 5).map(job => (
              <div key={job._id} className="flex justify-between items-center bg-white p-3 rounded-lg">
                <span className="font-medium text-gray-900">{job.title}</span>
                <span className="text-sm text-red-600">
                  Deadline: {new Date(job.deadline).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending Approvals (for HR Head) */}
      {(user?.role === 'hr_head' || user?.role === 'admin') && stats?.pendingApprovals > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-yellow-600" />
            <h3 className="font-semibold text-yellow-800">
              {stats.pendingApprovals} Job Request{stats.pendingApprovals > 1 ? 's' : ''} Pending Approval
            </h3>
          </div>
        </div>
      )}

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Job Status Overview */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4">Job Status Overview</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Draft</span>
              <span className="font-semibold">{stats?.jobs?.draft || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Pending Approval</span>
              <span className="font-semibold text-yellow-600">{stats?.jobs?.pending || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Approved (Active)</span>
              <span className="font-semibold text-green-600">{stats?.jobs?.approved || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Rejected</span>
              <span className="font-semibold text-red-600">{stats?.jobs?.rejected || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Closed</span>
              <span className="font-semibold text-gray-500">{stats?.jobs?.closed || 0}</span>
            </div>
          </div>
        </div>

        {/* Candidate Pipeline Summary */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4">Candidate Pipeline</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Sourced</span>
              <span className="font-semibold">{stats?.candidates?.sourced || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Screening</span>
              <span className="font-semibold">{stats?.candidates?.screening || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Interview</span>
              <span className="font-semibold">{stats?.candidates?.interview || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Selected</span>
              <span className="font-semibold text-blue-600">{stats?.candidates?.selected || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Offered</span>
              <span className="font-semibold text-green-600">{stats?.candidates?.offered || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Joined</span>
              <span className="font-semibold text-emerald-600">{stats?.candidates?.joined || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Jobs */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h3 className="font-semibold text-gray-900 mb-4">Recent Job Requests</h3>
        {recentJobs.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No job requests yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested By</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {recentJobs.map(job => (
                  <tr 
                    key={job._id} 
                    className="hover:bg-gray-50"
                  >
                    <td className="px-4 py-3 font-medium text-gray-900">{job.title}</td>
                    <td className="px-4 py-3 text-gray-600">{job.requestedBy?.name || 'N/A'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        job.status === 'approved' ? 'bg-green-100 text-green-800' :
                        job.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        job.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {job.status?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-sm">
                      {new Date(job.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewJob(job)}
                          className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                        >
                          View
                        </button>
                        {job.status === 'approved' && (
                          <button
                            onClick={() => navigate(`/dashboard/recruitment/pipeline/${job._id}`)}
                            className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            Manage Candidates
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Job Form Modal */}
      {showJobModal && (
        <JobFormModal
          onClose={() => setShowJobModal(false)}
          onSuccess={handleJobCreated}
        />
      )}

      {/* Job Detail Modal */}
      {showDetailModal && selectedJob && (
        <JobDetailModal
          job={selectedJob}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedJob(null);
          }}
          onUpdate={fetchDashboard}
        />
      )}
    </div>
  );
};

export default RecruitmentDashboard;
